import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Service for navigating to an external URL by updating the window object.
 *
 * This service is designed to address timeout issues during testing when directly using `window.location.href`.
 * By providing this service, we can easily mock and control method behavior in our tests, avoiding such issues.
 *
 */

@Injectable({
  providedIn: 'root'
})
export class WindowService {
  /**
   * Navigates the browser to the specified URL.
   *
   * @param href - The URL to navigate to.
   */
  navigateToHref(href: string): void {
    window.location.href = href;
  }

  getCurrentPage(): string {
    return window.location.href;
  }

  /**
   * Loads a URL as a fire-and-forget image beacon, completing on load or error.
   * Used to issue a same-site GET (e.g. cookie clearing) without navigating away.
   */
  loadBeacon(url: string): Observable<void> {
    return new Observable<void>((subscriber) => {
      const image = new Image();
      const finish = () => {
        subscriber.next();
        subscriber.complete();
      };
      image.onload = finish;
      image.onerror = finish;
      image.src = url;
    });
  }
}
